// ---- Fullscreen background shader: Cream <-> True Black, with vignette + film grain ----
export const bgVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0); // fullscreen NDC quad
  }
`;

export const bgFragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform float uDark;   // 0 = cream, 1 = black
  uniform float uTime;
  uniform vec3 uCream;
  uniform vec3 uBlack;
  uniform vec3 uRed;

  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }

  void main() {
    vec3 col = mix(uCream, uBlack, uDark);

    // soft radial light center that fades as we go dark
    float d = distance(vUv, vec2(0.5, 0.52));
    col += (1.0 - uDark) * (0.03 - d * 0.05);

    // faint red pulse living in the darkness (the 'noise' mood)
    float glow = smoothstep(0.7, 0.0, d) * uDark * 0.06;
    glow *= 0.6 + 0.4 * sin(uTime * 0.8);
    col += uRed * glow;

    // vignette in dark scene
    col *= 1.0 - d * 0.35 * uDark;

    // film grain
    float g = hash(vUv * vec2(1200.0, 900.0) + uTime * 60.0);
    col += (g - 0.5) * 0.025;

    gl_FragColor = vec4(col, 1.0);
  }
`;

// ---- Dust points: round, soft-edged motes with per-particle size variation ----
// THREE.PointsMaterial draws an unmasked square sprite, which reads as visible
// boxes against a dark background. These shaders mask each point to a disc with
// a soft falloff, and vary the size per particle so the field looks like dust
// rather than a grid of identical dots.
export const dustVertex = /* glsl */ `
  attribute float aScale;
  uniform float uSize;
  uniform float uScale;   // renderer height * 0.5, matches PointsMaterial attenuation
  varying float vScale;

  void main() {
    vScale = aScale;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * aScale * uScale / max(0.001, -mv.z);
  }
`;

export const dustFragment = /* glsl */ `
  precision highp float;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying float vScale;

  void main() {
    // gl_PointCoord is 0..1 across the sprite; mask it to a circle.
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;

    // Soft core with a long tail — squaring the falloff keeps the centre bright
    // while the edge dissolves, which is what reads as a mote of dust.
    float a = smoothstep(0.5, 0.0, d);
    a *= a;

    // Smaller motes sit further back in the field, so let them fade a little.
    a *= mix(0.55, 1.0, clamp(vScale, 0.0, 1.5) / 1.5);

    gl_FragColor = vec4(uColor, a * uOpacity);
  }
`;

// ---- Paper material for the hero brief card (subtle grain + soft shading) ----
export const paperVertex = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNormal;
  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const paperFragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  varying vec3 vNormal;
  uniform vec3 uPaper;

  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
  float noise(vec2 p){
    vec2 i = floor(p); vec2 f = fract(p);
    float a = hash(i), b = hash(i+vec2(1.0,0.0));
    float c = hash(i+vec2(0.0,1.0)), d = hash(i+vec2(1.0,1.0));
    vec2 u = f*f*(3.0-2.0*f);
    return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
  }

  void main() {
    float grain = noise(vUv * 420.0) * 0.05;
    float soft = noise(vUv * 5.0) * 0.05;
    vec3 col = uPaper - grain - soft * 0.4;
    float light = clamp(dot(vNormal, normalize(vec3(0.35, 0.75, 0.6))), 0.0, 1.0);
    col *= 0.92 + light * 0.16;
    gl_FragColor = vec4(col, 1.0);
  }
`;
